import React, { useMemo, useState } from 'react';
import { useFormik } from 'formik';
import type { ReviewProps } from './types';
import { useNavigate, useParams } from 'react-router-dom';
import { Accordion, Button, Form } from 'react-bootstrap';
import AccordionItem from 'components/common/AccordionItem';
import { ProjectDetails } from '../ProjectDetails';
import { ChecklistDetails } from '../ChecklistDetails';
import { Submission } from '../Submission';
import { useSelector } from 'react-redux';
import { RootStates } from 'redux/store';
import greenCircleImg from 'assets/images/greencircle.png';
import pendingImg from 'assets/images/pending.png';
import './Review.scss';
import Modal from 'components/common/Modal/Modal';
import { useTranslation } from 'react-i18next';

export const Review: React.FC<ReviewProps> = ({ project, onSubmit }) => {
  const { t } = useTranslation();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const navigate = useNavigate();
  const { projectId, inspectionId } = useParams();
  const submissionDetails = useSelector(
    (state: RootStates) => state.conductInspection.submissionDetails,
  );
  const questions = useSelector(
    (state: RootStates) => state.conductInspection.checklistDetails.questions,
  );
  const formik = useFormik({
    initialValues: {},
    onSubmit: (values) => {
      setShowConfirmation(true);
    },
  });
  const selectedChecklist = useSelector(
    (state: RootStates) =>
      state.conductInspection.projectDetails?.selectedChecklist,
  );
  const checklistFilledCount = useSelector(
    (state: RootStates) =>
      Object.values(
        state.conductInspection.checklistDetails.questions ?? {},
      ).filter((q) => q.response).length,
  );

  const isChecklistFilled = useMemo(
    () => checklistFilledCount === selectedChecklist?.data?.length,
    [checklistFilledCount, selectedChecklist?.data?.length],
  );

  const handleBack = () =>
    navigate(
      `/project/${project.projectInfo.id}/conduct-inspection/${inspectionId}/3`,
    );

  const handleSubmit = () => {
    onSubmit({
      projectId,
      inspectionId,
      page: '3',
      body: {
        ...submissionDetails,
        status: Object.values(questions ?? {}).some(
          (q) => q.requireReinspection,
        )
          ? 'OPEN'
          : 'CLOSE',
      },
    });
    setShowConfirmation(false);
  };

  const handleClose = () => setShowConfirmation(false);

  return (
    <Form
      className="review-details d-flex flex-column"
      onSubmit={formik.handleSubmit}
    >
      <div className="d-flex flex-column form-content">
        <Accordion>
          <AccordionItem
            eventKey="project_details"
            text={t('label_project_details')}
            leftAction={<img alt="completed" src={greenCircleImg} />}
            children={<ProjectDetails disabled noFooter project={project} />}
          />
          <AccordionItem
            eventKey="checklist_details"
            leftAction={
              <>
                {!isChecklistFilled ? (
                  <img alt="pending" src={pendingImg} />
                ) : (
                  <img alt="completed" src={greenCircleImg} />
                )}
              </>
            }
            text={`#${selectedChecklist?.name ?? ''} ${t('checklist')}`}
            children={<ChecklistDetails disabled noFooter project={project} />}
          />
          <AccordionItem
            eventKey="submission_details"
            text={t('submission')}
            leftAction={<img alt="completed" src={greenCircleImg} />}
            children={<Submission disabled noFooter project={project} />}
          />
        </Accordion>
      </div>
      <div className="d-flex justify-content-between">
        <Button onClick={handleBack}>{t('btn_back')}</Button>
        <Button type="submit">{t('submit')}</Button>
      </div>
      <Modal
        show={showConfirmation}
        withIcon
        type={!isChecklistFilled ? 'danger' : 'success'}
        title=""
        modalClassName="checklist-modal"
        submitBtnText={t('submit')}
        cancelBtnText={t('btn_goback')}
        onSubmit={handleSubmit}
        onClose={handleClose}
      />
    </Form>
  );
};
